// Typed fetch client for the Sensing API.
//
// Design constraints enforced here:
// - Requests go to the app origin; cookies are always sent with credentials
//   `include`, so the session cookie travels without exposing it to script.
// - Mutations echo the readable CSRF cookie in `X-CSRF-Token`.
// - Responses are parsed with the lossless parser so large integers survive.
// - Every failure becomes a typed `ApiError`; raw server text never reaches the
//   interface as copy.

import { ApiError, codeFromStatus, isKnownErrorCode } from './errors';
import { parseJson, JsonParseError } from '@/lib/json';

/** HTTP methods the client uses. */
export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export interface RequestOptions {
  method?: HttpMethod;
  /** JSON body; serialized with the client's own encoder. */
  body?: unknown;
  query?: Record<string, string | number | undefined>;
  signal?: AbortSignal;
  /** Skip the CSRF header even for a mutation (not currently used). */
  omitCsrf?: boolean;
}

const CSRF_COOKIE = 'sensing_csrf';
const CSRF_HEADER = 'X-CSRF-Token';

/** Read a cookie by name; the CSRF cookie is intentionally readable. */
function readCookie(name: string): string | null {
  const prefix = `${name}=`;
  for (const part of document.cookie.split(';')) {
    const trimmed = part.trim();
    if (trimmed.startsWith(prefix)) {
      return decodeURIComponent(trimmed.slice(prefix.length));
    }
  }
  return null;
}

/** Build a URL from a path and optional query values, dropping undefined. */
export function buildUrl(path: string, query?: RequestOptions['query']): string {
  if (!query) return path;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined) continue;
    params.set(key, String(value));
  }
  const search = params.toString();
  return search ? `${path}?${search}` : path;
}

/** Parse a `Retry-After` header, accepting seconds or an HTTP date. */
function parseRetryAfter(response: Response): number | undefined {
  const header = response.headers.get('Retry-After');
  if (!header) return undefined;
  const seconds = Number(header);
  if (Number.isFinite(seconds) && seconds >= 0) return seconds;
  const date = Date.parse(header);
  if (!Number.isNaN(date)) {
    return Math.max(0, Math.round((date - Date.now()) / 1000));
  }
  return undefined;
}

interface ServerErrorBody {
  code?: unknown;
  message?: unknown;
}

/**
 * Perform a request and decode the JSON body losslessly.
 *
 * The generic parameter is a caller-supplied expectation; because the contract
 * is authoritative, callers narrow with the generated types at the call site.
 */
export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = options.method ?? 'GET';
  const headers = new Headers({ Accept: 'application/json' });

  let bodyInit: BodyInit | undefined;
  if (options.body !== undefined) {
    headers.set('Content-Type', 'application/json');
    bodyInit = JSON.stringify(options.body);
  }

  const isMutation = method !== 'GET';
  if (isMutation && !options.omitCsrf) {
    const token = readCookie(CSRF_COOKIE);
    if (token) headers.set(CSRF_HEADER, token);
  }

  let response: Response;
  const init: RequestInit = {
    method,
    headers,
    credentials: 'include',
    // Never let a stale cached API response masquerade as fresh data.
    cache: 'no-store',
  };
  if (bodyInit !== undefined) init.body = bodyInit;
  if (options.signal) init.signal = options.signal;

  try {
    response = await fetch(buildUrl(path, options.query), init);
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === 'AbortError') {
      throw new ApiError({ code: 'aborted', cause });
    }
    throw new ApiError({ code: 'network_error', cause });
  }

  if (response.status === 204) {
    return undefined as T;
  }

  const text = await response.text();

  if (!response.ok) {
    const retryAfterSeconds = parseRetryAfter(response);
    let body: ServerErrorBody | null = null;
    if (text.length > 0) {
      try {
        const decoded = parseJson(text);
        if (decoded && typeof decoded === 'object') body = decoded;
      } catch {
        /* Non-JSON error bodies fall back to the status code. */
      }
    }
    throw new ApiError({
      code: isKnownErrorCode(body?.code) ? body.code : codeFromStatus(response.status),
      status: response.status,
      ...(retryAfterSeconds !== undefined ? { retryAfterSeconds } : {}),
      ...(typeof body?.message === 'string' ? { serverMessage: body.message } : {}),
    });
  }

  if (text.length === 0) {
    return undefined as T;
  }

  try {
    return parseJson(text) as T;
  } catch (cause) {
    if (cause instanceof JsonParseError) {
      throw new ApiError({ code: 'invalid_response', status: response.status, cause });
    }
    throw cause;
  }
}
